import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

export default function AdminLeaderboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leaderboard", { params: { limit: 100 } });
      setItems(data.items);
      const next = {};
      data.items.forEach((u) => {
        next[u.id] = {
          elo: u.elo,
          wins: u.wins,
          losses: u.losses,
          draws: u.draws,
          ranked_games: u.ranked_games,
        };
      });
      setEdits(next);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveStats = async (id) => {
    try {
      await api.patch(`/admin/users/${id}/stats`, edits[id]);
      toast.success("Classement mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="glass rounded-lg p-4 sm:p-6">
      <p className="text-sm text-[color:var(--text-secondary)] mb-6">
        Modifiez les scores ELO et statistiques. Les administrateurs apparaissent ici (masqués du classement public).
      </p>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[color:var(--primary)]" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-[color:var(--text-secondary)] border-b border-[color:var(--border)]">
                <th className="py-2">#</th>
                <th className="py-2">Joueur</th>
                <th className="py-2">ELO</th>
                <th className="py-2">V</th>
                <th className="py-2">D</th>
                <th className="py-2">N</th>
                <th className="py-2">Parties classées</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u, i) => (
                <tr key={u.id} className="border-b border-[color:var(--border)]/50">
                  <td className="py-2 text-[color:var(--text-secondary)]">{i + 1}</td>
                  <td className="py-2">
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-[color:var(--text-secondary)]">{u.email}</div>
                    {u.role === "admin" && <span className="text-[10px] text-amber-500">admin</span>}
                  </td>
                  {["elo", "wins", "losses", "draws", "ranked_games"].map((field) => (
                    <td key={field} className="py-2 pr-1">
                      <Input
                        type="number"
                        className="h-8 w-16 bg-[color:var(--surface)] border-[color:var(--border)]"
                        value={edits[u.id]?.[field] ?? 0}
                        onChange={(e) =>
                          setEdits((prev) => ({
                            ...prev,
                            [u.id]: { ...prev[u.id], [field]: parseInt(e.target.value, 10) || 0 },
                          }))
                        }
                      />
                    </td>
                  ))}
                  <td className="py-2">
                    <Button size="sm" variant="outline" className="h-8" onClick={() => saveStats(u.id)}>
                      <Save size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
